import { useEffect, useRef, useState } from 'react';
import { navigate } from '../App';
import type { ConnectionStatus } from '../types';

type Props = {
  topic: string;
  canEditTopic: boolean;
  status: ConnectionStatus;
  onTopicChange: (topic: string) => void;
};

const STATUS_LABEL: Record<ConnectionStatus, string> = {
  connecting: 'Connecting…',
  online: 'Live',
  offline: 'Reconnecting…',
  kicked: 'Removed',
};

const ShareButton = () => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const share = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Clipboard access needs a secure context and can still be denied;
      // fall back to a prompt so the link is always obtainable.
      window.prompt('Copy this link', url);
      return;
    }
    setCopied(true);
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button type="button" className="btn btn-ghost" onClick={() => void share()}>
      {copied ? '✓ Link copied' : 'Copy invite link'}
    </button>
  );
};

type TopicFieldProps = {
  topic: string;
  canEdit: boolean;
  onTopicChange: (topic: string) => void;
};

const TopicField = ({ topic, canEdit, onTopicChange }: TopicFieldProps) => {
  const [draft, setDraft] = useState(topic);
  const [editing, setEditing] = useState(false);

  // While someone else is editing, keep following the server value.
  useEffect(() => {
    if (!editing) setDraft(topic);
  }, [topic, editing]);

  if (!canEdit) {
    return <p className="topic topic-static">{topic || 'Untitled round'}</p>;
  }

  return (
    <form
      className="topic-form"
      onSubmit={(event) => {
        event.preventDefault();
        setEditing(false);
        onTopicChange(draft);
      }}
    >
      <input
        className="topic-input"
        value={draft}
        placeholder="What are we estimating?"
        maxLength={140}
        onFocus={() => setEditing(true)}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          setEditing(false);
          onTopicChange(draft);
        }}
      />
    </form>
  );
};

export const RoomHeader = ({ topic, canEditTopic, status, onTopicChange }: Props) => (
  <header className="room-header">
    <a
      href="/"
      className="brand"
      onClick={(event) => {
        event.preventDefault();
        navigate('/');
      }}
    >
      <span className="brand-mark">♠</span>
      <span className="brand-text">Planning Poker</span>
    </a>

    <TopicField topic={topic} canEdit={canEditTopic} onTopicChange={onTopicChange} />

    <div className="room-header-right">
      <span className={`status status-${status}`}>
        <i className="status-dot" aria-hidden="true" />
        {STATUS_LABEL[status]}
      </span>
      <ShareButton />
    </div>
  </header>
);
